import { DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoadingController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { ToastComponent } from 'src/app/core-files/loaders/toast-messages';
import { LeadsService } from 'src/app/services/leads.service';
import { SharedServices } from 'src/app/services/shared.service';
import { DialogInfoDetailsComponent } from 'src/app/shared-module/components/dialog-info-details/dialog-info-details.component';

@Component({
  selector: 'app-export-property-leads',
  templateUrl: './export-property-leads.component.html',
  styleUrls: ['./export-property-leads.component.scss'],
  standalone: false
})
export class ExportPropertyLeadsComponent {
  selectedFile: File | null = null;
  fileError: string | null = null;
  isUploading = false;
  progress = 0;
  allowedExtensions = ['xlsx', 'xls'];
  sampleFilePath = 'assets/ImportLeadsPropertyFile.xlsx';
  // Reactive date range form and bounds
  dateRangeForm: FormGroup;
  today = new Date();
  // Dynamic min/max bounds for toDate based on fromDate
  get toDateMin(): Date | null {
    const from = this.fromDateCtrl?.value as Date;
    return from ? new Date(from.getFullYear(), from.getMonth(), from.getDate()) : null;
  }
  get toDateMax(): Date | null {
    const from = this.fromDateCtrl?.value as Date;
    if (!from) return null;
    const max = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    max.setDate(max.getDate() + 90);
    return max;
  }


  constructor(
    private dialogRef: MatDialogRef<ExportPropertyLeadsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private leadsService: LeadsService,
    private sharedServices: SharedServices,
    private loadingController: LoadingController,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private toastComponent: ToastComponent,
    private matDialog: MatDialog
  ) {
    // Build reactive form: fromDate (start) and toDate (end) both required, no future, max span 90 days
    this.dateRangeForm = this.fb.group({
      fromDate: [new Date(), [Validators.required, this.noFutureValidator.bind(this)]],
      // To Date: only required; window enforced via min/max bindings + maxRangeValidator
      toDate: [null, [Validators.required]]
    }, { validators: this.maxRangeValidator.bind(this) });
    // Reset toDate whenever fromDate changes so user must re-select within new 90-day window
    this.fromDateCtrl?.valueChanges.subscribe({ next: (from: Date) => {
      if (!from) return;
      // Clear selected toDate (also clears validation errors tied to stale value)
      this.toDateCtrl?.reset(null, { emitEvent: true });
    } });
  }

  async export() {
    const formData = new FormData();
    formData.append('UserId', this.sharedServices.getAgentId());
    formData.append('file', this.selectedFile);

    const loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Importing leads...'
    });
    await loading.present();
    this.isUploading = true;
    this.progress = 10;
    if (this.data.type === 'Lead') {

      this.leadsService.ExportMyLeads().subscribe({
        next: async (res: any) => {
          await loading.dismiss();
        },
        error: async () => {
          await loading.dismiss();
        }
      });
    } else {
      let exportData = {
        UserTypeId: 2,
        RangeFrom: this.datePipe.transform(
          this.fromDateCtrl?.value,
          "yyyy-MM-dd"
        ),
        RangeTo: this.datePipe.transform(
          this.toDateCtrl?.value,
          "yyyy-MM-dd"
        )
      };
      if (Capacitor.getPlatform() === 'web') {
      this.leadsService.ExportAgentProperty(exportData).subscribe({
        next: async (res: any) => {
            try {
              await loading.dismiss();

              const blob: Blob = res.body;
              if (!(blob instanceof Blob) || blob.size === 0) {
                this.toastComponent.showToast('File empty or invalid', 'danger');
                return;
              }

              // Detect unexpected JSON (means server returned error JSON but you asked for blob)
              if (blob.type === 'application/json') {
                const text = await blob.text();
                console.error('Server returned JSON instead of file:', text);
                this.toastComponent.showToast('Export failed (server returned JSON)', 'danger');
                return;
              }

              // Try filename from headers
              let fileName = 'ExportedProperties.xlsx';
              const cd = res.headers?.get?.('Content-Disposition');
              if (cd) {
                const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
                if (m && m[1]) fileName = m[1].replace(/['"]/g, '');
              }              
                // Web: trigger browser download
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                this.toastComponent.showToast('Export Properties successfully', 'success');
                this.dialogRef.close();
              
            } catch (e) {
              await loading.dismiss();
              this.toastComponent.showToast('Download error', 'danger');
            }
         
        },
        error: async (err) => {
          await loading.dismiss();
          this.toastComponent.showToast('No Properties Found', 'danger');
        }
      });
    } else {
       await loading.dismiss();
          this.matDialog.open(DialogInfoDetailsComponent, {
            data: { dialogTitle: "Mobile App Support", dialogDesc: "The property export feature is currently available only on the web; support for the mobile app is a work in progress. You can use https://intercitybroker.com to export properties.." },
          });
    }
    }
  }
  close() {
    this.dialogRef.close(false);
  }

  // ---- Validators ----
  private noFutureValidator(control: AbstractControl): ValidationErrors | null {
    const val: Date = control.value;
    if (!val) return null;
    return val > this.today ? { future: true } : null;
  }

  private maxRangeValidator(group: AbstractControl): ValidationErrors | null {
    const fromDate: Date = group.get('fromDate')?.value;
    const toDate: Date = group.get('toDate')?.value;
    if (!fromDate || !toDate) return null;
    // Normalize
    const start = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const end = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
    if (end < start) return { order: true };
    const diffDays = (end.getTime() - start.getTime()) / 86400000;
    if (diffDays > 90) return { rangeTooLong: true };
    return null;
  }

  // Convenience getters for template
  get fromDateCtrl() { return this.dateRangeForm.get('fromDate'); }
  get toDateCtrl() { return this.dateRangeForm.get('toDate'); }
}
